import React from 'react';
import { format, parseISO, differenceInDays, isAfter, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';

export const EMPTY_CELL = '—';

export function isEmptyCell(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

export function coalesce(...values) {
  for (const value of values) {
    if (!isEmptyCell(value)) return value;
  }
  return null;
}

export function cellText(value, fallback = EMPTY_CELL) {
  return isEmptyCell(value) ? fallback : value;
}

export function renderCell(value, fallback = EMPTY_CELL) {
  if (isEmptyCell(value)) {
    return <span className="text-gray-400">{fallback}</span>;
  }
  return value;
}

export function renderCellDate(date, formatStr = 'dd/MM/yyyy') {
  if (isEmptyCell(date)) {
    return <span className="text-gray-400">{EMPTY_CELL}</span>;
  }
  return formatDate(date, formatStr);
}

export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date) return EMPTY_CELL;
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatStr, { locale: fr });
  } catch (error) {
    return EMPTY_CELL;
  }
};

export const getDaysUntil = (date) => {
  if (!date) return null;
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return differenceInDays(dateObj, new Date());
  } catch (error) {
    return null;
  }
};

export const isExpiringSoon = (date, daysThreshold = 30) => {
  const days = getDaysUntil(date);
  return days !== null && days >= 0 && days <= daysThreshold;
};

export const isExpired = (date) => {
  if (!date) return false;
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isBefore(dateObj, new Date());
  } catch (error) {
    return false;
  }
};

export const getEquipmentStatus = (equipment) => {
  if (!equipment.nextMaintenanceDate) return 'operational';
  
  const daysUntil = getDaysUntil(equipment.nextMaintenanceDate);
  
  if (daysUntil === null) return 'operational';
  if (daysUntil < 0) return 'expired';
  if (daysUntil <= 7) return 'maintenance_required';
  if (daysUntil <= 30) return 'expiring_soon';
  
  return 'operational';
};

export const getStatusColor = (status) => {
  const colors = {
    operational: 'text-green-600 bg-green-100',
    maintenance_required: 'text-orange-600 bg-orange-100',
    expired: 'text-red-600 bg-red-100',
    expiring_soon: 'text-yellow-600 bg-yellow-100',
    out_of_service: 'text-gray-600 bg-gray-100',
  };
  return colors[status] || colors.operational;
};

export const getStatusLabel = (status) => {
  const labels = {
    operational: 'Conforme',
    maintenance_required: 'Maintenance requise',
    expired: 'Expiré',
    expiring_soon: 'Expire bientôt',
    out_of_service: 'Hors service',
  };
  return labels[status] || 'Inconnu';
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  const cleaned = phone.replace(/[\s\-()./]/g, '');
  return /^\+?[0-9]{8,15}$/.test(cleaned);
};

export const generatePassword = (length = 12) => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const special = '!@#$%^&*';
  const allChars = lowercase + uppercase + digits + special;
  
  // Ensure at least one of each type
  let password = '';
  password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
  password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
  password += digits.charAt(Math.floor(Math.random() * digits.length));
  password += special.charAt(Math.floor(Math.random() * special.length));
  
  // Fill the rest randomly
  for (let i = 4; i < length; i++) {
    password += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Convert camelCase to snake_case for API requests
export const toSnakeCase = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toSnakeCase);
  
  const snakeCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    snakeCaseObj[snakeKey] = value;
  }
  return snakeCaseObj;
};

// Convert snake_case to camelCase for API responses
export const toCamelCase = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  
  const camelCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    camelCaseObj[camelKey] = value;
  }
  return camelCaseObj;
};

